import React, { KeyboardEvent } from 'react';
import { capitalize } from '../../utils/strings';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import Input from '../../components/inputs/Input';
import { Link } from 'react-router-dom';
import { Role, User } from '../../types/userTypes';
import { Country, Region, RegionsList } from '../../types/orderTypes';
import { ButtonComponent } from '../../ReusableComponents/Button/ButtonComponent';
import { downloadFile } from '../../utils/fetchDownload';
import { supportedCountries } from '../../utils/country';
import { getAuthHeadersFromCookie } from '../../lib/AuthClient/cookie';

export function RenderPaginationHeader({
  loading,
  searchText,
  setSearchText,
  page,
  pageSize,
  items,
  total,
  totalPages,
  roleFilter,
  regionFilter,
  countryFilter,
  changePage,
  roleFilterChange,
  toggleShowLockedUsers,
  regionFilterChange,
  countryFilterChange,
  showLockedUsers,
  triggerSearchLoad,
  limitedAccess,
}: {
  loading: boolean;
  searchText: string;
  setSearchText: any;
  page: number;
  pageSize: number;
  items: User[];
  total: number;
  totalPages: number;
  roleFilter: Role[];
  regionFilter?: Region | null;
  countryFilter?: Country | null;
  limitedAccess?: boolean;
  changePage: (offset: number) => void;
  roleFilterChange: (role: Role[]) => void;
  toggleShowLockedUsers: (show: boolean) => void;
  regionFilterChange: (region?: Region) => void;
  countryFilterChange: (country?: Country) => void;
  triggerSearchLoad: () => void;
  showLockedUsers: boolean;
}): JSX.Element {
  const itemsPerPage = (page - 1) * pageSize;
  const authHeaders = getAuthHeadersFromCookie();

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      triggerSearchLoad();
    }
  };

  return (
    <>
      <div className="user--list__header">
        <Input
          className="user-list__header-search"
          placeholder="Search text..."
          onChange={event => setSearchText(event.target.value)}
          onKeyPress={handleKeyPress}
          value={searchText}
          autoFocus={searchText !== ''}
          onRef={input => searchText !== '' && input && input.focus()}
          disabled={loading}
        />
        <ButtonComponent
          className="user-list__header-search-button"
          text="Search"
          onClick={triggerSearchLoad}
        />
        <div className="user-list__header-role role">
          <select
            onChange={event => {
              const selectedRole = (event.target.value as Role) || null;
              const roles = selectedRole == null ? [] : [selectedRole];
              if (selectedRole === 'SENDER') {
                roles.push('SENDER_ADMIN');
              }
              roleFilterChange(roles);
            }}
            value={
              roleFilter.length === 0
                ? ''
                : roleFilter.includes('SENDER')
                ? 'SENDER'
                : roleFilter[0]
            }
          >
            <option value="">All roles</option>
            <option value="ADMIN">Admins</option>
            <option value="COURIER">Couriers</option>
            <option value="SENDER">Senders</option>
          </select>
        </div>
        <div className="user-list__header-role regions">
          <select
            onChange={event =>
              regionFilterChange((event.target.value as Region) || null)
            }
            value={regionFilter || ''}
          >
            {!limitedAccess && <option value="">All regions</option>}
            {RegionsList.filter(region =>
              limitedAccess
                ? authHeaders.availableRegions.includes(region)
                : true
            ).map(region => (
              <option key={region} value={region}>
                {capitalize(region)}
              </option>
            ))}
          </select>
        </div>
        <div className="user-list__header-role countries">
          <select
            onChange={event =>
              countryFilterChange((event.target.value as Country) || null)
            }
            value={countryFilter || ''}
          >
            <option value="">All countries</option>
            {Object.keys(supportedCountries).map(countryName => {
              const countryInfo = supportedCountries[countryName];
              return (
                <option
                  key={countryName}
                  value={countryInfo.countryCode.toLowerCase()}
                >
                  {supportedCountries[countryName].name}
                </option>
              );
            })}
          </select>
        </div>
        <div className="user--list__buttons-wrapper">
          <ButtonComponent
            className="user--list__locked-users-button"
            variant="outlined"
            onClick={() => toggleShowLockedUsers(!showLockedUsers)}
          >
            {showLockedUsers ? 'Show all users' : 'Show locked users'}
          </ButtonComponent>
          <ButtonComponent
            className="user--list__create-user-button"
            variant="outlined"
            component={Link}
            to="/users/create"
          >
            Create User
          </ButtonComponent>
          <ButtonComponent
            className="user--list__download-couriers-csv"
            variant="outlined"
            onClick={async () =>
              await downloadFile('courier/csv-list', 'couriers-data.csv')
            }
          >
            Download Couriers CSV
          </ButtonComponent>
        </div>
        <div className="user--list__pagination-nav">
          <FaAngleLeft
            className="user--list__pagination-navbut"
            onClick={() => changePage(-1)}
            style={{
              width: 30,
              height: 30,
              color: loading || page <= 1 ? 'rgba(0,0,0,0.26)' : '#3a3d4b',
            }}
          />
          {!loading && itemsPerPage > -1 && (
            <p className="user--list__pagination-info">
              {itemsPerPage + 1}-{itemsPerPage + items.length} of {total}
            </p>
          )}
          <FaAngleRight
            className="user--list__pagination-navbut"
            onClick={() => changePage(1)}
            style={{
              width: 30,
              height: 30,
              color:
                loading || page >= totalPages ? 'rgba(0,0,0,0.26)' : '#3a3d4b',
            }}
          />
        </div>
      </div>
    </>
  );
}
